import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import {
  ApprovalFormTable,
  ApprovalSectionToggle,
} from '@/components/Approval/@components/ApprovalFormTable';
import {
  APPROVAL_SECTION_GAP,
  InfoSectionTitle,
} from '@/components/Approval/@components/InfoSection';
import {
  formatEntNumber,
  type EntExpenseAttributes,
} from '@/utils/approval/entExpense';
import BudgetSummaryTable from './BudgetSummaryTable';
import TotalAmountTable from './TotalAmountTable';
import { DetailCard } from './styles';

type EntExpenseInfoProps = {
  data: EntExpenseAttributes;
};

/** 213 조회 — 저장값(작성 시점 라벨·금액) 그대로 */
const EntExpenseInfo = ({ data }: EntExpenseInfoProps) => {
  const { t } = useTranslation();
  const [detailOpen, setDetailOpen] = useState(false);
  const detailTotal = Math.round(
    data.details.reduce((sum, d) => sum + d.cost, 0),
  );

  return (
    <Box className="ent-expense-view">
      <Box sx={{ mb: '10px' }}>
        <InfoSectionTitle sx={{ mb: 1 }}>{t('예산 항목')}</InfoSectionTitle>
        <BudgetSummaryTable
          budgetItem={data.budgetItemLabel}
          budgetYear={data.budgetYear}
          total={data.budgetTotal}
          remaining={data.budgetRemaining}
          usageRate={data.budgetUsageRate}
        />
      </Box>
      <TotalAmountTable rows={data.totalAmount} />

      <Box sx={{ mt: `${APPROVAL_SECTION_GAP}px` }}>
        <ApprovalSectionToggle
          title={t('지급 정보')}
          open={detailOpen}
          onToggle={() => setDetailOpen((v) => !v)}
        >
          {data.details.length > 0 && (
            <DetailCard sx={{ mt: '10px' }}>
              <ApprovalFormTable className="ent-detail-table">
                <colgroup>
                  <col style={{ width: 202 }} />
                  <col style={{ width: 202 }} />
                  <col style={{ width: 140 }} />
                  <col />
                </colgroup>
                <thead>
                  <tr>
                    <th className="align-left">{t('분류')}</th>
                    <th className="align-left">{t('항목')}</th>
                    <th className="align-right">{t('비용')}</th>
                    <th className="align-left">{t('비고')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.details.map((d, idx) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <tr key={idx}>
                      <td className="align-left">{d.category.label || '-'}</td>
                      <td className="align-left">{d.item.label || '-'}</td>
                      <td className="align-right">
                        {formatEntNumber(Math.round(d.cost))}
                      </td>
                      <td className="align-left">{d.remark}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="align-left" colSpan={2}>
                      <strong>{t('총계')}</strong>
                    </td>
                    <td className="align-right">
                      <strong>{formatEntNumber(detailTotal)}</strong>
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </ApprovalFormTable>
            </DetailCard>
          )}
        </ApprovalSectionToggle>
      </Box>
    </Box>
  );
};

export default EntExpenseInfo;
export { default as BudgetSummaryTable } from './BudgetSummaryTable';
export { default as TotalAmountTable } from './TotalAmountTable';
export * from './styles';
